import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { type FilterOption } from '@/types/advanceFilter';
import { type TagFilterMode, toTagKeyValues } from './tagKeyValueUtils';

/**
 * What a tag is called in one mode: its key alone, or the `key=value` pair the
 * options carry — a value-less tag included, as `key=`. Selections are held as these
 * strings, so identifying a tag and identifying a selection is the same question.
 */
const tagOptionId = (tag: {
  key: string;
  value?: string | null;
}, mode: TagFilterMode) => mode === 'key' ? tag.key : `${tag.key}=${tag.value || ''}`;

/**
 * Turns the discovered tag list into the selectable options for one filter mode, shared by the tags
 * condition popover and the tags full panel so both offer the same options for the same search terms.
 */
export const useTagOptions = t0 => {
  const $ = _c(29);
  const {
    tags,
    selectedValues,
    keyFilter,
    valueFilter,
    mode
  } = t0;
  let t1;
  if ($[0] !== mode || $[1] !== tags) {
    let t2;
    if ($[3] !== mode) {
      t2 = tag => tagOptionId(tag, mode);
      $[3] = mode;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = new Set((tags ?? []).map(t2));
    $[0] = mode;
    $[1] = tags;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const discovered = t1;
  let t2;
  if ($[5] !== discovered || $[6] !== selectedValues) {
    let t3;
    if ($[8] !== discovered) {
      t3 = value => !discovered.has(value);
      $[8] = discovered;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = new Set(selectedValues.filter(t3));
    $[5] = discovered;
    $[6] = selectedValues;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const undiscoveredIds = t2;
  let t3;
  if ($[10] !== tags) {
    t3 = tags ?? [];
    $[10] = tags;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3 || $[13] !== undiscoveredIds) {
    t4 = [...t3, ...toTagKeyValues([...undiscoveredIds])];
    $[12] = t3;
    $[13] = undiscoveredIds;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const allTags = t4;
  let t5;
  if ($[15] !== allTags) {
    t5 = new Set(allTags.map(_temp));
    $[15] = allTags;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const distinctKeys = t5;
  let t6;
  if ($[17] !== distinctKeys || $[18] !== keyFilter) {
    t6 = Array.from(distinctKeys).filter(key => !keyFilter || key.toLowerCase().includes(keyFilter.toLowerCase())).map(_temp2);
    $[17] = distinctKeys;
    $[18] = keyFilter;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const keys = t6;
  let t7;
  if ($[20] !== allTags || $[21] !== keyFilter || $[22] !== valueFilter) {
    const t8 = allTags.filter(t => (!keyFilter || t.key.toLowerCase().includes(keyFilter.toLowerCase())) && (!valueFilter || !!t.value?.toLowerCase().includes(valueFilter.toLowerCase()))).map(_temp3);
    let t9;
    if ($[24] !== t8) {
      t9 = new Set(t8);
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    const allValues = Array.from(t9);
    t7 = allValues.map(_temp4);
    $[20] = allTags;
    $[21] = keyFilter;
    $[22] = valueFilter;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const keyValuePairs = t7;
  const t8 = mode === "key" ? keys : keyValuePairs;
  let t9;
  if ($[26] !== t8 || $[27] !== undiscoveredIds) {
    t9 = {
      options: t8,
      undiscoveredIds
    };
    $[26] = t8;
    $[27] = undiscoveredIds;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
function _temp(tag_0) {
  return tag_0.key;
}
function _temp2(key_0) {
  return {
    id: key_0,
    name: key_0
  };
}
function _temp3(tag_1) {
  return tagOptionId(tag_1, "key-value");
}
function _temp4(value_0) {
  return {
    id: value_0,
    name: value_0
  };
}